/**
 * VendorPicker — the single canonical vendor typeahead used across procurement.
 *
 * Design rules (Round 4, P1):
 *   • Search-as-you-type on /vendors/search (no free text)
 *   • Approved vendors are selectable, pending are shown greyed with a
 *     "Request empanelment" link, blacklisted/suspended hidden.
 *   • Emits { id, name, empanelment_status, ... } via onSelect.
 *
 * Props:
 *   value           : selected vendor id (uuid string)
 *   displayName     : optional cached name (used when value is set but the row
 *                     hasn't been fetched)
 *   onSelect(v)     : selected vendor row from /vendors/search (or null on clear)
 *   allowPending    : boolean — when true, pending rows are selectable
 *                     (used by the empanelment queue view)
 *   vendorType      : optional server-side type filter
 *   placeholder     : input placeholder
 *   testid          : root data-testid for the picker
 *   disabled        : disables the input
 *   invalid         : renders a rose ring for validation errors
 *
 * The component is intentionally self-contained: no external state,
 * no lifted "vendors" array. Every caller previously loaded /vendors?limit=2000
 * on mount — this replaces that.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Search, X as XIcon, ShieldCheck, Clock, ExternalLink } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';

const badge = (status) => {
  if (status === 'approved')
    return <span className="inline-flex items-center gap-0.5 text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"><ShieldCheck size={9} />Approved</span>;
  if (status === 'blacklisted')
    return <span className="text-[10px] font-display font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">Blacklisted</span>;
  if (status === 'suspended')
    return <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">Suspended</span>;
  return <span className="inline-flex items-center gap-0.5 text-[10px] font-display font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded"><Clock size={9} />Pending</span>;
};

export default function VendorPicker({
  value = '',
  displayName = '',
  onSelect,
  allowPending = false,
  vendorType = null,
  placeholder = 'Search vendor by name, code or GSTIN…',
  testid = 'vendor-picker',
  disabled = false,
  invalid = false,
}) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cachedName, setCachedName] = useState(displayName || '');
  const [requesting, setRequesting] = useState(null);
  const wrapRef = useRef(null);
  const debRef = useRef(null);

  // Load cached selected name when only an id is provided (e.g. on edit).
  useEffect(() => {
    if (!value) { setCachedName(''); return; }
    if (displayName) { setCachedName(displayName); return; }
    let cancelled = false;
    apiGet(`/vendors/${value}`).then((v) => {
      if (!cancelled) setCachedName(v?.name || '');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [value, displayName]);

  // Debounced remote search.
  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q: query };
        if (vendorType) params.vendor_type = vendorType;
        const list = await apiGet('/vendors/search', params);
        setRows(Array.isArray(list) ? list : []);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debRef.current);
  }, [query, open, vendorType]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (v) => {
    const st = (v.empanelment_status || '').toLowerCase();
    if (st === 'blacklisted' || st === 'suspended') {
      toast.error(`${v.name} is ${st} — cannot be used.`);
      return;
    }
    if (st !== 'approved' && !allowPending) {
      toast.error(`${v.name} is not yet empanelled. Request empanelment first.`);
      return;
    }
    onSelect?.(v);
    setCachedName(v.name || '');
    setQuery('');
    setOpen(false);
  };

  const requestEmpanel = async (v) => {
    setRequesting(v.id);
    try {
      await apiPost(`/vendors/${v.id}/empanel-request`, {});
      toast.success(`Empanelment requested for ${v.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to request empanelment');
    } finally { setRequesting(null); }
  };

  const clear = () => { onSelect?.(null); setCachedName(''); setQuery(''); };

  const shown = value ? (cachedName || 'Loading…') : query;

  return (
    <div className="relative" ref={wrapRef} data-testid={testid}>
      <div className={`flex items-center border rounded-sm bg-white px-2 ${invalid ? 'border-rose-400 ring-1 ring-rose-200' : 'border-slate-300 focus-within:border-[#3b4872]'} ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        <Search size={13} className="text-slate-400 mr-1.5" />
        <input
          type="text"
          value={value ? shown : query}
          onFocus={() => { setOpen(true); if (value) { onSelect?.(null); setCachedName(''); } }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder={placeholder}
          className="flex-1 py-2 text-sm outline-none bg-transparent"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {value && (
          <button type="button" onClick={clear} className="text-slate-400 hover:text-slate-700 ml-1" data-testid={`${testid}-clear`}>
            <XIcon size={13} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && <div className="px-3 py-3 text-xs text-slate-400 italic">Searching…</div>}
          {!loading && rows.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-400 italic">
              {query ? `No vendor matches "${query}"` : 'Type to search vendors'}
            </div>
          )}
          {!loading && rows.map((v) => {
            const st = (v.empanelment_status || 'pending').toLowerCase();
            const selectable = st === 'approved' || (allowPending && st === 'pending');
            return (
              <div
                key={v.id}
                onClick={() => selectable && pick(v)}
                className={`flex items-start gap-2 px-3 py-2 border-t border-slate-100 first:border-t-0 transition-colors ${
                  selectable ? 'cursor-pointer hover:bg-slate-50' : 'opacity-60 cursor-not-allowed bg-slate-50/40'
                }`}
                data-testid={`${testid}-option-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {v.name}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                    <span className="font-mono">{v.vendor_code || '—'}</span>
                    {v.gstin && <span className="font-mono">GST: {v.gstin}</span>}
                    {badge(st)}
                  </div>
                </div>
                {st === 'pending' && !allowPending && (
                  <button
                    type="button"
                    disabled={requesting === v.id}
                    onClick={(e) => { e.stopPropagation(); requestEmpanel(v); }}
                    className="text-[10px] font-display font-bold uppercase tracking-wider text-[#3b4872] hover:underline flex items-center gap-1 disabled:opacity-40 whitespace-nowrap"
                    data-testid={`${testid}-empanel-req-${v.id}`}
                  >
                    <ExternalLink size={10} />
                    {requesting === v.id ? 'Requesting…' : 'Request empanelment'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
